import { useEffect, useMemo, useRef } from 'react';
import { ErrorOutlineRounded, RefreshRounded, SearchOffRounded } from '@mui/icons-material';
import { SuperSearchResult } from 'api';
import { OverflowTooltip } from 'components/ui';
import { SUPER_SEARCH_MIN_REMOTE_SEARCH_LENGTH } from './SuperSearch.constants';
import { getSuperSearchResultIcon } from './SuperSearch.icons';
import {
  getBroaderSearchLabel,
  getEmptyStateCopy,
  getOrderedSubtitleSegments,
  getResultsAnnouncement,
  getResultTone,
  getResultTypeLabel,
  getSectionTone,
} from './SuperSearch.presentation';
import {
  AssistiveText,
  EmptyActionButton,
  EmptyActions,
  EmptyDescription,
  EmptyIcon,
  EmptyState,
  EmptyTitle,
  ErrorDescription,
  ErrorIcon,
  ErrorRetryButton,
  ErrorState,
  ErrorTitle,
  KeyCap,
  PartialErrorBanner,
  PartialErrorCopy,
  PartialErrorDescription,
  PartialErrorIcon,
  PartialErrorRetryButton,
  PartialErrorTitle,
  ResultButton,
  ResultCopy,
  ResultIcon,
  ResultLabel,
  ResultMeta,
  ResultsList,
  ResultsRoot,
  ResultSubtitle,
  ResultSubtitleSegment,
  ResultTypeBadge,
  SearchGuidance,
  SearchLoadingLabel,
  SearchLoadingState,
  SearchSkeletonCopy,
  SearchSkeletonIcon,
  SearchSkeletonLine,
  SearchSkeletonRow,
  SectionLabel,
  SectionRoot,
} from './SuperSearch.styles';
import { SUPER_SEARCH_TEST_IDS } from './SuperSearch.testIds';
import type {
  SuperSearchContext,
  SuperSearchDisplayItem,
  SuperSearchPendingPage,
  SuperSearchSection,
} from './SuperSearch.types';

const SEARCH_SKELETON_ROWS = ['primary', 'secondary', 'tertiary'] as const;

interface SuperSearchResultsProps {
  context: SuperSearchContext;
  firmIconUrlsById: ReadonlyMap<number, string>;
  hasFatalError: boolean;
  hasPartialError: boolean;
  isKeyboardNavigation: boolean;
  isLoading: boolean;
  isSearching: boolean;
  isShowingSuggestedDestinations: boolean;
  matchingItemCount: number;
  onActivateItem: (item: SuperSearchDisplayItem) => void;
  onBroadenSearch: () => void;
  onClearSearch: () => void;
  onRetrySearch: () => void;
  onSelectItem: (itemIndex: number) => void;
  partialErrorDescription: string;
  pendingPage?: SuperSearchPendingPage;
  query: string;
  searchErrorDescription: string;
  selectedIndex: number;
  visibleSections: SuperSearchSection[];
}

const SuperSearchResults = ({
  context,
  firmIconUrlsById,
  hasFatalError,
  hasPartialError,
  isKeyboardNavigation,
  isLoading,
  isSearching,
  isShowingSuggestedDestinations,
  matchingItemCount,
  onActivateItem,
  onBroadenSearch,
  onClearSearch,
  onRetrySearch,
  onSelectItem,
  partialErrorDescription,
  pendingPage,
  query,
  searchErrorDescription,
  selectedIndex,
  visibleSections,
}: SuperSearchResultsProps) => {
  const resultsRootRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLButtonElement>(null);
  const normalizedQuery = query.trim();
  const broaderSearchLabel = getBroaderSearchLabel(context, pendingPage);
  const { description: emptyDescription, title: emptyTitle } = getEmptyStateCopy(normalizedQuery);
  const visibleItems = useMemo(() => visibleSections.flatMap(section => section.items), [visibleSections]);
  const itemIndexById = useMemo(
    () => new Map(visibleItems.map((item, itemIndex) => [item.id, itemIndex] as const)),
    [visibleItems]
  );
  const resultsAnnouncement = getResultsAnnouncement({
    hasFatalError,
    hasPartialError,
    isEmptyQuery: normalizedQuery.length === 0,
    isLoading,
    resultCount: matchingItemCount,
    suggestedDestinationCount: isShowingSuggestedDestinations ? visibleItems.length : 0,
  });
  const shouldShowSearchGuidance =
    normalizedQuery.length > 0 && normalizedQuery.length < SUPER_SEARCH_MIN_REMOTE_SEARCH_LENGTH;

  // Query or scope changes reset only the results viewport owned by this component.
  useEffect(() => {
    resultsRootRef.current?.scrollTo({ top: 0 });
  }, [context.company?.id, context.firm?.id, pendingPage?.key, query]);

  // Keyboard navigation keeps the active option visible without moving combobox focus.
  useEffect(() => {
    if (!isKeyboardNavigation) {
      return;
    }

    selectedItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isKeyboardNavigation, selectedIndex]);

  return (
    <ResultsRoot aria-busy={isSearching} ref={resultsRootRef}>
      <AssistiveText
        aria-atomic="true"
        aria-live="polite"
        data-testid={SUPER_SEARCH_TEST_IDS.status}
        id={SUPER_SEARCH_TEST_IDS.status}
        role="status">
        {resultsAnnouncement}
      </AssistiveText>
      {isLoading && (
        <SearchLoadingState data-testid={SUPER_SEARCH_TEST_IDS.loadingState}>
          <SearchLoadingLabel>Searching available content...</SearchLoadingLabel>
          {SEARCH_SKELETON_ROWS.map(row => (
            <SearchSkeletonRow aria-hidden key={row}>
              <SearchSkeletonIcon animation="wave" variant="rounded" />
              <SearchSkeletonCopy>
                <SearchSkeletonLine animation="wave" variant="rounded" width="34%" />
                <SearchSkeletonLine animation="wave" variant="rounded" width="58%" />
              </SearchSkeletonCopy>
            </SearchSkeletonRow>
          ))}
        </SearchLoadingState>
      )}
      {hasFatalError && !isSearching && (
        <ErrorState data-testid={SUPER_SEARCH_TEST_IDS.error}>
          <ErrorIcon aria-hidden>
            <ErrorOutlineRounded />
          </ErrorIcon>
          <ErrorTitle>We couldn&apos;t load your results</ErrorTitle>
          <ErrorDescription>{searchErrorDescription}</ErrorDescription>
          <ErrorRetryButton data-testid={SUPER_SEARCH_TEST_IDS.retry} onClick={onRetrySearch}>
            <RefreshRounded aria-hidden />
            Retry
          </ErrorRetryButton>
        </ErrorState>
      )}
      {hasPartialError && !isSearching && (
        <PartialErrorBanner data-testid={SUPER_SEARCH_TEST_IDS.error}>
          <PartialErrorIcon aria-hidden>
            <ErrorOutlineRounded />
          </PartialErrorIcon>
          <PartialErrorCopy>
            <PartialErrorTitle>Some results couldn&apos;t load</PartialErrorTitle>
            <PartialErrorDescription>{partialErrorDescription}</PartialErrorDescription>
          </PartialErrorCopy>
          <PartialErrorRetryButton data-testid={SUPER_SEARCH_TEST_IDS.retry} onClick={onRetrySearch}>
            <RefreshRounded aria-hidden />
            Retry
          </PartialErrorRetryButton>
        </PartialErrorBanner>
      )}
      {shouldShowSearchGuidance && matchingItemCount > 0 && !hasFatalError && (
        <SearchGuidance>
          Continue typing to include versions, saved views, valuation approaches, and documents.
        </SearchGuidance>
      )}
      {!isLoading && !hasFatalError && matchingItemCount === 0 && (
        <EmptyState data-testid={SUPER_SEARCH_TEST_IDS.empty}>
          <EmptyIcon aria-hidden>
            <SearchOffRounded />
          </EmptyIcon>
          <EmptyTitle>{emptyTitle}</EmptyTitle>
          <EmptyDescription>{emptyDescription}</EmptyDescription>
          {normalizedQuery.length > 0 && (
            <EmptyActions>
              {broaderSearchLabel && (
                <EmptyActionButton data-testid={SUPER_SEARCH_TEST_IDS.broaden} onClick={onBroadenSearch}>
                  {broaderSearchLabel}
                </EmptyActionButton>
              )}
              <EmptyActionButton data-testid={SUPER_SEARCH_TEST_IDS.clear} onClick={onClearSearch}>
                Clear search
              </EmptyActionButton>
            </EmptyActions>
          )}
        </EmptyState>
      )}
      <ResultsList
        aria-label="Super Search results"
        data-testid={SUPER_SEARCH_TEST_IDS.results}
        id={SUPER_SEARCH_TEST_IDS.results}
        role="listbox">
        {!isLoading &&
          !hasFatalError &&
          visibleSections.map(section => {
            const sectionLabelId = SUPER_SEARCH_TEST_IDS.sectionLabel(section.id);

            return (
              <SectionRoot aria-labelledby={sectionLabelId} key={section.id} role="group">
                <SectionLabel id={sectionLabelId} tone={getSectionTone(section.id)}>
                  {section.label}
                </SectionLabel>
                {section.items.map(item => {
                  const itemIndex = itemIndexById.get(item.id) ?? 0;
                  const isSelected = itemIndex === selectedIndex;
                  const subtitleSegments = getOrderedSubtitleSegments(item);
                  const resultTone = getResultTone(item, section.id);

                  return (
                    <ResultButton
                      aria-label={`${item.label}. ${subtitleSegments
                        .map(({ segment }) => segment)
                        .join(' / ')}. ${getResultTypeLabel(item)}`}
                      aria-selected={isSelected}
                      data-testid={SUPER_SEARCH_TEST_IDS.result(item.id)}
                      id={SUPER_SEARCH_TEST_IDS.result(item.id)}
                      isSelected={isSelected}
                      key={item.id}
                      onClick={() => onActivateItem(item)}
                      onMouseMove={() => onSelectItem(itemIndex)}
                      ref={isSelected ? selectedItemRef : undefined}
                      role="option"
                      tabIndex={-1}>
                      <ResultIcon aria-hidden tone={resultTone}>
                        {getSuperSearchResultIcon(item, section.id, firmIconUrlsById)}
                      </ResultIcon>
                      <ResultCopy>
                        <OverflowTooltip placement="top-start" title={item.label}>
                          <ResultLabel>{item.label}</ResultLabel>
                        </OverflowTooltip>
                        <ResultSubtitle isSelected={isSelected}>
                          {subtitleSegments.map(({ originalIndex, segment, tone: subtitleTone }) => (
                            <OverflowTooltip
                              key={`${item.id}:subtitle:${originalIndex}`}
                              placement="top-start"
                              title={segment}>
                              <ResultSubtitleSegment
                                preserveWidth={
                                  item.result.type === SuperSearchResult.type.VALUATION_APPROACH && originalIndex === 0
                                }
                                tone={subtitleTone}>
                                {segment}
                              </ResultSubtitleSegment>
                            </OverflowTooltip>
                          ))}
                        </ResultSubtitle>
                      </ResultCopy>
                      <ResultMeta aria-hidden>
                        <ResultTypeBadge tone={resultTone}>{getResultTypeLabel(item)}</ResultTypeBadge>
                        {isSelected && <KeyCap>↵</KeyCap>}
                      </ResultMeta>
                    </ResultButton>
                  );
                })}
              </SectionRoot>
            );
          })}
      </ResultsList>
    </ResultsRoot>
  );
};

export default SuperSearchResults;
